import {
    collection,
    component,
    dateField,
    EntityData,
    field,
    identifier,
    LocalisableEntityData,
    reloadable
} from "@fedeltapointofsale/data-client";
import {PriceLevelReferenceData} from "../../pricing/price-level/price-level.data";
import {ContactReferenceData} from "../../contact/contact.data";
import {AccountAddressBlockType} from "../account-address-block-type";

export class AccountPinData extends EntityData<AccountPinData> {
    @identifier accountPinId: number;
    @component contact: ContactReferenceData;
    @field pin: string;
    @field defaultContact: boolean;
    @field invoiceContact: boolean;
    @field statementContact: boolean;
}

export class AccountPaymentDetailsData extends EntityData<AccountPaymentDetailsData> {
    @dateField paymentDate: Date;
    @field paymentAmt: number;
}

export class AccountPaymentData extends EntityData<AccountPaymentData> {
    @component details: AccountPaymentDetailsData;
}

export class AccountChargeData extends EntityData<AccountChargeData> {
    @identifier chargeId: number;
    @dateField chargeDate: Date;
    @field chargeAmt: number;
    @field hasOpenCharges: boolean;
}

export class AccountHistoryData extends EntityData<AccountHistoryData> {
    @collection(AccountChargeData) lastTrx: AccountChargeData[];
    @collection(AccountPaymentData) lastPayment: AccountPaymentData[];
}

export class AccountInvoiceData extends EntityData<AccountInvoiceData> {
    @field billingCycleDbValue: string;
    @field addressBlockTypeDbValue: string = AccountAddressBlockType.ACCOUNT_NAME_AND_NO;
    @field terms: number;
    @field termsComment: string;
    @field footerComment: string;
    @field headerComment: string;
    @field creditFooterComment: string;
    @field noStatement: boolean;
    @field noInvoice: boolean;
}

export class AccountType extends EntityData<AccountType> {
    @identifier id: boolean;
    @field accountLimited: boolean;
    @field limitDuration: boolean;
    @field name: string;
    @field description: string;
    @collection() validBillingCycles: string[]
    @field issueStatements: boolean;
    @field issueInvoices: boolean;
    @field accountingType: number;
}

export class AccountDiscountData extends EntityData<AccountDiscountData> {
    @identifier discountId: number;
    @field name: string;
    @field discontinued: boolean;
}

export class AccountOutletData extends EntityData<AccountOutletData> {
    @identifier outletId: number;
    @field name: string;
    @field description: string;
}

export class AccountDetailsData extends EntityData<AccountDetailsData> {
    public static readonly LIMIT_DURATION_DAILY = 0;
    public static readonly LIMIT_DURATION_WEEKLY = 1;
    public static readonly LIMIT_DURATION_MONTHLY = 2;
    public static readonly LIMIT_DURATION_YEARLY = 3;

    @field accountNo: number;
    @field accountName: string;
    @field creditLimit: number;
    @field limitDuration: number;
    @field abn: string;
    @dateField expiry: Date;
    @field currentBalance: number;
    @field creditAmtTotal: number;
    @dateField fromDate: Date;
    @field locked: boolean;
    @field requiresCustomerReference: boolean;
    @field discontinued: boolean;
    @component primaryOutlet: AccountOutletData;
    @component priceLevel: PriceLevelReferenceData;
    @component discount: AccountDiscountData;
    @component @reloadable accountType: AccountType;
}

export class AccountData extends LocalisableEntityData<AccountData> {
    @identifier accountId: string;
    @component details: AccountDetailsData;
    @component invoiceDetails: AccountInvoiceData;
    @component accountHistoryData: AccountHistoryData;
    @collection(AccountPinData) accountPins: AccountPinData[];

    // used in the validation
    accountNoAuto: boolean;

    get hasOpenCharges(): boolean {
        return this.accountHistoryData && this.accountHistoryData.lastTrx && this.accountHistoryData.lastTrx.length > 0 && this.accountHistoryData.lastTrx[0].hasOpenCharges;
    }

    get accountName(): string {
        return this.details.accountName;
    }

    set accountName(value: string) {
        this.details.accountName = value;
    }
}
